import { Component, Input, OnInit } from '@angular/core';
import { TranslateService }         from '@ngx-translate/core';
import { UIRouter }                 from '@uirouter/core';

import { SessionCompanyModel }     from '../sessions/models/session.company';
import { SessionService }          from '../sessions/session.service';
import { Table }                   from '../table/types/table';
import { TableQueryParameters }    from '../table/types/table-query-parameters';
import { TableService }            from '../table/table.service';
import { VehicleActionsComponent } from './vehicle.actions.component';
import { VehicleModel }            from './models/vehicle';
import { VehiclesFiltersModel }    from './typings/vehicles.filters';
import { VehiclesService }         from './vehicles.service';

@Component({
  selector   : 'wor-vehicles',
  template: require('./vehicles.component.html')
})
export class VehiclesComponent implements OnInit {
  @Input() vehicles: Array<VehicleModel>;

  company       : SessionCompanyModel;
  filters       : VehiclesFiltersModel;
  fleetCodeLabel: string;
  regoLabel     : string;

  tableSettings = {} as Table;

  constructor (
    private router          : UIRouter,
    private sessionService  : SessionService,
    private tableService    : TableService,
    private translateService: TranslateService,
    private vehiclesService : VehiclesService
  ) {}

  ngOnInit () : void {
    this.company        = this.sessionService.company();
    this.fleetCodeLabel = this.company.company_variable_fields.fleet_code_name || this.translateService.instant('VEHICLE_SPACE.FLEET_CODE');
    this.regoLabel      = this.company.company_variable_fields.plate_number_name || this.sessionService.countrySettings().rego_label;

    this.filters = this.tableService.setFiltersFromCache(this.vehiclesService.tableCacheKey, {
        archivedOnly: false,
        value       : ''
      });

    this.initTableSettings();
  }

  createBooking ( vehicle : VehicleModel ) : void {
    this.router.stateService.go('app.event', {
      customer_id: vehicle.customer_id,
      vehicle_id : vehicle.id,
    });
  }

  createInvoice ( vehicle : VehicleModel ) : void {
    this.router.stateService.go('app.invoice', {
      customer_id: vehicle.customer_id,
      vehicle_id : vehicle.id,
    });
  }

  filterVehicles () : void {
    if ( window[ this.vehiclesService.broadcastKey ] ) {
      window[ this.vehiclesService.broadcastKey ](this.filters);
    }
  }

  getVehicles ( params : TableQueryParameters ) : Promise<Array<VehicleModel>> {
    return this.vehiclesService.get(params, this.filters)
      .then(vehicles => {
        this.vehicles = vehicles;

        return vehicles;
      });
  }

  initTableSettings () : void {
    this.tableSettings = {
      broadcast: this.vehiclesService.broadcastKey,
      cache    : {
        enabled: true,
        key    : this.vehiclesService.tableCacheKey
      },
      columns: [
        {
          classes: 'all-caps',
          field  : 'plate_number',
          header : {
            text: this.regoLabel
          }
        },
        {
          field : 'make',
          header: {
            text: this.translateService.instant('VEHICLE_SPACE.MAKE')
          }
        },
        {
          field : 'model',
          header: {
            text: this.translateService.instant('VEHICLE_SPACE.MODEL')
          }
        },
        {
          field : 'display_name',
          header: {
            text: this.translateService.instant('JS_SPACE.COLUMNS.CUSTOMER_NAME')
          }
        },
        {
          component: {
            bindings: {
              outputs: {
                onBookingClick: ( vehicle : VehicleModel ) => this.createBooking(vehicle),
                onEditClick   : ( vehicle : VehicleModel ) => this.viewVehicle(vehicle),
                onInvoiceClick: ( vehicle : VehicleModel ) => this.createInvoice(vehicle)
              }
            },
            enabled: true,
            factory: VehicleActionsComponent,
          },
          sortable: false,
        }
      ],
      empty: {
        text: this.translateService.instant('QUERY_DROPDOWN_SPACE.GRID.NO_VEHICLES_FOUND')
      },
      pagination: {
        enabled   : true,
        pageSize  : this.tableService.getCachedPaging(this.vehiclesService.tableCacheKey).pageSize || 10,
        pageSizes : [ 10, 25, 50 ],
        queryCount: () => this.vehiclesService.getCount(this.filters)
      },
      rows: {
        highlight: {
          enabled: true
        }
      },
      sorting: this.tableService.setSortingFromCache(this.vehiclesService.tableCacheKey, {
        direction: 'asc',
        field    : 'plate_number'
      })
    };

    if ( this.company.company_settings.show_fleet_code ) {
      this.tableSettings.columns?.push({
        field : 'fleet_code',
        header: {
          text: this.fleetCodeLabel
        }
      });
    }
  }

  viewVehicle ( vehicle : VehicleModel ) : void {
    this.router.stateService.go('app.vehicle', {
      customer_id: vehicle.customer_id,
      id         : vehicle.id,
    });
  }
}
